import { Button } from "@/components/ui/button";
import { ArrowRight, Star, ShieldCheck, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { HeroImage } from "@/components/ui/HeroImage";

export const Hero = () => {
  const navigate = useNavigate();
  
  return (
    <section className="relative min-h-[500px] md:min-h-[600px] lg:min-h-[70vh] flex items-start md:items-center pt-36 md:pt-32 pb-16">
      <HeroImage
        src="/images/lifetime-movers-hero-truck-v2.jpg"
        alt="LifeTime Movers truck with crew loading boxes outside Oak Brook Plaza"
        priority={true}
        overlayClassName="bg-gradient-to-r from-primary/95 via-primary/85 to-primary/70"
        responsiveWidths={[800, 1200]}
        baseWidth={1920}
        sizes="100vw"
      />

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl">
          {/* Google Review Badge */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <a 
              href="https://www.google.com/search?q=lifetime+movers+chicago"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-secondary/30 px-4 py-2 rounded-full border border-secondary/40 hover:bg-secondary/40 transition-colors duration-200"
            >
              <div className="flex items-center gap-0.5" aria-hidden="true">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star} 
                    className="h-4 w-4" 
                    fill="#FBBF24" 
                    stroke="#FBBF24"
                  />
                ))}
              </div>
              <span className="text-sm font-semibold text-primary-foreground">Rated 4.9 Stars from 10,000+ Moves</span>
            </a>
            <a
              href="https://www.bbb.org/us/il/oak-brook/profile/moving-companies/lifetime-movers-0654-1000106466"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-primary-foreground/15 px-4 py-2 rounded-full border border-primary-foreground/30 hover:bg-primary-foreground/25 transition-colors duration-200"
            >
              <ShieldCheck className="h-5 w-5 text-secondary" />
              <span className="text-sm font-semibold text-primary-foreground">A+ BBB Accredited Provider</span>
            </a>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground mb-6 leading-tight">
            Midwest Movers &amp; Local Chicago Moving Company<br />
            <span className="text-secondary">Pick Up IL, IN, WI & MI to Anywhere in the USA.</span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-primary-foreground/90 mb-6 leading-relaxed">
            LifeTime Movers (Lifetime Moving Co.) — Chicago's trusted lifetime moving company serving Illinois, Indiana, Wisconsin, and Michigan with professional moving and storage services. Delivery to all 48 states.
          </p>

          <a href="tel:6306869987" className="inline-flex items-center gap-2 text-xl md:text-2xl font-bold text-orange-400 hover:text-orange-300 mb-8 drop-shadow-lg">
            <Phone className="h-6 w-6" />
            Call for Instant Help: (630) 686-9987
          </a>



          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="hero"
              size="xl"
              onClick={() => {
                const target = document.getElementById('instant-estimate');
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                  navigate('/quote');
                }
              }}
            >
              Get Instant Estimate
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="xl"
              className="bg-primary-foreground/15 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/25"
              asChild
            >
              <a href="https://www.lifetimemovers.us/lifetime-movers-reviews" target="_blank" rel="noopener noreferrer">
                View Reviews
                <span aria-hidden="true" className="inline-flex">
                  <Star className="h-4 w-4 ml-2 fill-secondary text-secondary" />
                  <Star className="h-4 w-4 ml-1 fill-secondary text-secondary" />
                  <Star className="h-4 w-4 ml-1 fill-secondary text-secondary" />
                  <Star className="h-4 w-4 ml-1 fill-secondary text-secondary" />
                  <Star className="h-4 w-4 ml-1 fill-secondary text-secondary" />
                </span>
              </a>
            </Button>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
            <div className="flex items-center gap-3 bg-primary-foreground/15 px-4 py-3 rounded-lg border border-primary-foreground/20">
              <div className="text-secondary text-2xl font-bold">✓</div>
              <div>
                <div className="text-primary-foreground font-semibold">Licensed & Insured</div>
                <div className="text-primary-foreground/80 text-sm">Full protection</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-primary-foreground/15 px-4 py-3 rounded-lg border border-primary-foreground/20">
              <div className="text-secondary text-2xl font-bold">✓</div>
              <div>
                <div className="text-primary-foreground font-semibold">Nationwide Service</div>
                <div className="text-primary-foreground/80 text-sm">Coast to coast</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-primary-foreground/15 px-4 py-3 rounded-lg border border-primary-foreground/20">
              <div className="text-secondary text-2xl font-bold">✓</div>
              <div>
                <div className="text-primary-foreground font-semibold">Expert Team</div>
                <div className="text-primary-foreground/80 text-sm">Professional movers</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
